"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Container } from "@/components/layout/container";

const inspirations = [
  {
    title: "Les accords de couleurs",
    text: "Je suis attirée par les teintes qui se répondent avec intensité : des harmonies chaleureuses, des complémentaires vibrantes, des nuances qui trouvent leur équilibre sans jamais devenir trop sages.",
  },
  {
    title: "Les matières et les reflets",
    text: "La texture de la laine, sa douceur, sa manière d’absorber la couleur ou de révéler une pointe de lumière influencent naturellement chacune de mes compositions.",
  },
  {
    title: "Les atmosphères",
    text: "Certaines palettes évoquent une saison, une soirée, une lumière de fin de journée, un souvenir ou une sensation plus instinctive. C’est souvent là que tout commence.",
  },
];

const references = [
  {
    label: "Chaleur",
    text: "Des palettes habitées, jamais fades, avec une présence colorée qui reste délicate et maîtrisée.",
  },
  {
    label: "Relief",
    text: "Des contrastes subtils, des profondeurs, des éclats et parfois une touche brillante pour faire vibrer l’ensemble.",
  },
  {
    label: "Émotion",
    text: "Je cherche toujours la palette qui donne immédiatement envie de commencer, de toucher, d’imaginer un ouvrage.",
  },
  {
    label: "Originalité",
    text: "Un univers artistique, vivant et personnel, sans effet excessif, sans dureté, avec une vraie cohérence visuelle.",
  },
];

const monoFont = { fontFamily: '"IBM Plex Mono", monospace' };

const pagePalette = {
  background:
    "linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)",
} as React.CSSProperties;

function Reveal({
  children,
  delay = 0,
  className = "",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.16,
        rootMargin: "0px 0px -8% 0px",
      }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0px)" : "translateY(28px)",
        transition: `opacity 700ms ease, transform 700ms ease ${delay}ms`,
        willChange: "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

function ImageCard({
  src,
  alt,
  className = "",
  priority = false,
  sizes = "(max-width: 768px) 100vw, 50vw",
}: {
  src: string;
  alt: string;
  className?: string;
  priority?: boolean;
  sizes?: string;
}) {
  return (
    <div
      className={`relative overflow-hidden rounded-[28px] border border-[var(--border)] bg-[linear-gradient(180deg,rgba(244,215,231,0.96),rgba(247,225,235,0.94)_38%,rgba(245,210,227,0.92))] shadow-[var(--shadow)] ${className}`}
    >
      <Image
        src={src}
        alt={alt}
        fill
        priority={priority}
        sizes={sizes}
        className="object-cover transition duration-700 ease-out hover:scale-[1.03]"
      />
    </div>
  );
}

export default function SourcesDInspirationPage() {
  return (
    <div style={pagePalette}>
      <HeroSection />
      <InspirationGridSection />
      <ReferencesSection />
      <ClosingSection />
    </div>
  );
}

function HeroSection() {
  return (
    <section className="section overflow-hidden pt-8 sm:pt-10 lg:pt-12">
      <Container>
        <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center lg:gap-14">
          <Reveal className="max-w-[760px]">
            <p
              style={monoFont}
              className="text-[11px] uppercase tracking-[0.24em] text-[var(--accent)]/90"
            >
              Sources d’inspiration
            </p>

            <h1 className="mt-4 max-w-[11ch] text-5xl leading-[0.9] sm:text-6xl xl:text-7xl">
              Des palettes qui naissent d’une ambiance et d’un élan visuel.
            </h1>

            <p className="mt-6 max-w-[33rem] text-base leading-7 text-[var(--foreground)]/76 sm:text-lg">
              Mon inspiration se construit par couches : une chaleur de couleur, une sensation,
              un contraste, une matière, parfois un éclat. C’est ici que l’on comprend d’où viennent
              les harmonies et pourquoi certaines associations s’imposent.
            </p>

            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                href="/travail-de-teinturiere"
                className="rounded-full bg-[var(--primary)] px-6 py-3 text-sm font-medium text-white transition hover:opacity-90"
              >
                Voir mon travail de teinturière
              </Link>

              <Link
                href="/boutique"
                className="rounded-full border border-[rgba(217,90,47,0.22)] bg-[rgba(217,90,47,0.12)] px-6 py-3 text-sm font-medium text-[var(--foreground)] transition hover:border-[var(--accent)] hover:text-[var(--accent)]"
              >
                Explorer la boutique
              </Link>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="relative mx-auto w-full max-w-[560px]">
              <div className="relative aspect-[4/4.8] overflow-hidden rounded-[28px] shadow-[var(--shadow)]">
                <Image
                  src="/images/7.webp"
                  alt="Univers inspirant Jour J Création"
                  fill
                  priority
                  sizes="(max-width: 1024px) 100vw, 42vw"
                  className="object-cover object-center"
                />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(209,15,115,0.08)_0%,rgba(255,255,255,0)_30%,rgba(217,90,47,0.10)_100%)]" />
              </div>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

function InspirationGridSection() {
  return (
    <section className="section pt-0">
      <Container>
        <div className="grid gap-6 lg:grid-cols-[1.02fr_0.98fr]">
          <Reveal className="rounded-[30px] border border-[var(--border)] bg-[linear-gradient(180deg,rgba(244,215,231,0.96),rgba(247,225,235,0.94)_38%,rgba(245,210,227,0.92))] p-7 shadow-[var(--shadow)] sm:p-8">
            <p
              style={monoFont}
              className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85"
            >
              Ce qui nourrit mes collections
            </p>
            <h2 className="mt-4 max-w-2xl text-4xl leading-tight sm:text-5xl">
              Une recherche de vibration, de chaleur et d’équilibre dans chaque composition.
            </h2>

            <div className="mt-8 grid gap-5">
              {inspirations.map((item, index) => (
                <Reveal key={item.title} delay={index * 90}>
                  <article className="rounded-[24px] border border-[var(--border)] bg-[linear-gradient(180deg,rgba(244,215,231,0.96),rgba(247,225,235,0.94)_38%,rgba(245,210,227,0.92))] p-5">
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="text-2xl leading-tight">{item.title}</h3>
                      <span
                        style={monoFont}
                        className="text-[11px] uppercase tracking-[0.18em] text-[var(--primary)]"
                      >
                        0{index + 1}
                      </span>
                    </div>
                    <p className="mt-4 max-w-2xl text-sm leading-6 text-[var(--foreground)]/75">
                      {item.text}
                    </p>
                  </article>
                </Reveal>
              ))}
            </div>
          </Reveal>

          <div className="grid gap-6">
            <Reveal delay={220}>
              <ImageCard
                src="/images/8.webp"
                alt="Inspiration colorée et texture des laines"
                className="aspect-[5/6]"
                sizes="(max-width: 1024px) 100vw, 35vw"
              />
            </Reveal>
          </div>
        </div>
      </Container>
    </section>
  );
}

function ReferencesSection() {
  return (
    <section className="section">
      <Container>
        <Reveal className="mb-10 max-w-2xl">
          <p
            style={monoFont}
            className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85"
          >
            Ma direction créative
          </p>
          <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
            Un univers sobre et artistique, avec du relief, de la chaleur et une vraie présence.
          </h2>
        </Reveal>

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {references.map((item, index) => (
            <Reveal key={item.label} delay={index * 90}>
              <article className="rounded-[24px] border border-[var(--border)] bg-[linear-gradient(180deg,rgba(244,215,231,0.96),rgba(247,225,235,0.94)_38%,rgba(245,210,227,0.92))] p-6 shadow-[var(--shadow)]">
                <p
                  style={monoFont}
                  className="text-[11px] uppercase tracking-[0.18em] text-[var(--primary)]"
                >
                  {item.label}
                </p>
                <p className="mt-4 text-sm leading-7 text-[var(--foreground)]/74">
                  {item.text}
                </p>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={140} className="mt-8 rounded-[30px] border border-[var(--border)] bg-[linear-gradient(180deg,rgba(244,215,231,0.96),rgba(247,225,235,0.94)_38%,rgba(245,210,227,0.92))] p-6 shadow-[var(--shadow)] sm:p-7">
          <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div>
              <p
                style={monoFont}
                className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85"
              >
                Intention
              </p>
              <p className="mt-4 max-w-2xl text-base leading-7 text-[var(--foreground)]/74">
                J’aime les couleurs qui assument leur présence sans devenir trop fortes,
                les harmonies qui accrochent le regard avec délicatesse et les palettes qui donnent
                immédiatement envie de créer. C’est cette émotion-là que je cherche à transmettre
                à travers mes kits et mes pelotes.
              </p>
            </div>

            <div className="flex flex-wrap gap-4 lg:justify-end">
              <Link
                href="/specificites-des-produits"
                className="rounded-full border border-[rgba(217,90,47,0.22)] bg-[rgba(217,90,47,0.12)] px-5 py-3 text-sm font-medium transition hover:border-[var(--primary)] hover:text-[var(--primary)]"
              >
                Voir les spécificités
              </Link>
              <Link
                href="/boutique"
                className="rounded-full bg-[var(--primary)] px-5 py-3 text-sm font-medium text-white transition hover:opacity-90"
              >
                Explorer la boutique
              </Link>
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

function ClosingSection() {
  return (
    <section className="section pt-0">
      <Container>
        <div className="rounded-[34px] border border-[var(--border)] bg-[linear-gradient(180deg,rgba(244,215,231,0.96),rgba(247,225,235,0.94)_38%,rgba(245,210,227,0.92))] p-8 shadow-[var(--shadow)] sm:p-10">
          <Reveal className="max-w-3xl">
            <p
              style={monoFont}
              className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85"
            >
              Continuer l’exploration
            </p>

            <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
              Mes inspirations deviennent ensuite des gestes, des bains de teinture et des compositions prêtes à vivre chez vous.
            </h2>

            <p className="mt-6 max-w-2xl text-base leading-7 text-[var(--foreground)]/74">
              Cette page raconte la source sensible du projet. La suivante montre comment cette intuition prend forme dans mon travail de teinturière, puis dans les laines proposées en boutique.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                href="/travail-de-teinturiere"
                className="rounded-full bg-[var(--primary)] px-6 py-3 text-sm font-medium text-white transition hover:opacity-90"
              >
                Découvrir mon travail
              </Link>

              <Link
                href="/boutique"
                className="rounded-full border border-[rgba(217,90,47,0.22)] bg-[rgba(217,90,47,0.12)] px-6 py-3 text-sm font-medium transition hover:border-[var(--accent)] hover:text-[var(--accent)]"
              >
                Voir la boutique
              </Link>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}